import { ref } from '@vue/composition-api';
import { DialogPlugin } from 'tdesign-vue';
import { ErrorCircleFilledIcon } from 'tdesign-icons-vue';
import i18n, { i18n_t } from '@/locales/i18n';
import CustomDialog from '@/renderer/scripts/utils/CustomDialog';
import { FileInfo, TeamPathInfo } from '@/renderer/scripts/utils/upload/types';

enum OperateType {
  skip = 'skip',
  rename = 'rename',
  overwrite = 'overwrite',
}
export default function openConflictDialog(
  currFile: FileInfo,
  folderAndFiles: FileInfo[],
  overwriteDisabled: boolean,
): Promise<{
  isSame: boolean;
  operate: OperateType;
}> {
  return new Promise((resolve, reject) => {
    const operate = ref<OperateType>(overwriteDisabled ? OperateType.rename : OperateType.overwrite);
    const isSame = ref(false);
    const dialog = CustomDialog.confirm({
      closeOnOverlayClick: false,
      header: currFile.isDir ? i18n_t('重名文件夹') : i18n_t('重名文件'),
      body: h => {
        const checkDiv = (
          <div class="mt-16" style="display: flex; justify-content:space-between; align-items:center;">
            <t-checkbox
              checked={isSame.value}
              onChange={(val: boolean) => {
                isSame.value = val;
              }}>
              {i18n_t('为之后重名文件/文件夹执行此操作')}
            </t-checkbox>
          </div>
        );
        const basename = currFile.isDir ? i18n_t('Special.文件夹') : i18n_t('Special.文件');
        const overwriteMessage = {
          header: currFile.isDir ? i18n_t('合并重名文件夹') : i18n_t('覆盖重名文件'),
          content: currFile.isDir
            ? i18n_t('新上传同名文件为新版本覆盖，不同名文件新增至文件夹中')
            : i18n_t('文件将按照新上传的内容进行覆盖，生成一个新的文件版本'),
        };
        const blockStyle = {
          content: {
            color: 'rgba(0, 0, 0, 0.9)',
            marginBottom: '8px',
          },
          tip: {
            color: 'rgba(0, 0, 0, 0.6)',
            fontSize: '12px',
            lineHeight: '20px',
            marginTop: '8px',
            marginLeft: '26px',
          },
        };
        return (
          <div>
            <div class="flex-start" style="margin: 18px 0 32px; color: rgba(0, 0, 0, 0.9);">
              <ErrorCircleFilledIcon class="mr-5" size="18px" color="#FFA100" />
              {i18n_t('当前位置已存在【{slot1}】。', {
                slot1: currFile.name,
              })}
            </div>
            <div class="mb-24">
              <section style={blockStyle.content}>
                <t-radio
                  checked={operate.value === OperateType.overwrite}
                  value={OperateType.overwrite}
                  disabled={overwriteDisabled}
                  onChange={() => {
                    operate.value = OperateType.overwrite;
                  }}>
                  {overwriteMessage.header}
                </t-radio>
                <div style={blockStyle.tip}>{overwriteMessage.content}</div>
              </section>
              <section style={blockStyle.content}>
                <t-radio
                  checked={operate.value === OperateType.rename}
                  value={OperateType.rename}
                  onChange={() => {
                    operate.value = OperateType.rename;
                  }}>
                  {i18n_t('保留重名{slot1}', { slot1: basename })}
                </t-radio>
                <div style={blockStyle.tip}>
                  {i18n_t('新上传的重名{slot1} 将自动在其名称后面添加“序号”', { slot1: basename })}
                </div>
              </section>
              {folderAndFiles.length > 0 && (
                <section style={blockStyle.content}>
                  <t-radio
                    checked={operate.value === OperateType.skip}
                    value={OperateType.skip}
                    onChange={() => {
                      operate.value = OperateType.skip;
                    }}>
                    {i18n_t('跳过重名{slot1}', { slot1: basename })}
                  </t-radio>
                  <div style={blockStyle.tip}>{i18n_t('重名{slot1}将不会上传至当前位置', { slot1: basename })}</div>
                </section>
              )}
            </div>
            {folderAndFiles.length > 0 && checkDiv}
          </div>
        );
      },
      onConfirm: () => {
        if (dialog.hide) {
          dialog.hide();
        }
        resolve({
          isSame: isSame.value,
          operate: operate.value,
        });
      },
      onClose: () => {
        dialog && dialog.hide?.();
        reject(new Error('stop'));
      },
    });
  });
}
